import { CALIBRATION_SESSION_SEQUENCE } from './calendarLayout'
import { clearCalibrationCalendarStorage } from './resetCalibrationState'
import type { CalibrationSessionType } from '../calibrationTypes'
import { supabase } from '../supabaseClient'

/** Completed sessions must form a prefix of the fixed sequence (1 → 2 → 3 → 4). */
export function consecutiveCompletedSessions(recordedTypes: CalibrationSessionType[]): number {
  const recorded = new Set(recordedTypes)
  let count = 0
  for (const sessionType of CALIBRATION_SESSION_SEQUENCE) {
    if (!recorded.has(sessionType)) break
    count++
  }
  return count
}

export function orphanCalibrationMetrics(recordedTypes: CalibrationSessionType[]): CalibrationSessionType[] {
  const validCount = consecutiveCompletedSessions(recordedTypes)
  const allowed = new Set(CALIBRATION_SESSION_SEQUENCE.slice(0, validCount))
  return recordedTypes.filter((type) => !allowed.has(type))
}

/**
 * Aligns profile.calibration_completed_sessions and browser calendar cache with
 * calibration_metrics rows. Removes out-of-order metrics (e.g. cardio only).
 */
export async function reconcileCalibrationState(userId: string): Promise<{
  repaired: boolean
  error: string | null
}> {
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('calibration_completed_sessions, calibration_status')
    .eq('id', userId)
    .single()

  if (profileError || !profile) {
    return { repaired: false, error: profileError?.message ?? 'Profile not found' }
  }

  if (profile.calibration_status === 'completed') {
    return { repaired: false, error: null }
  }

  const dbCount = Number(profile.calibration_completed_sessions ?? 0)
  if (profile.calibration_status === 'not_started' && dbCount > 0) {
    const { error: resetError } = await supabase
      .from('profiles')
      .update({
        calibration_completed_sessions: 0,
        calibration_status: 'not_started',
      })
      .eq('id', userId)

    if (resetError) {
      return { repaired: false, error: resetError.message }
    }

    clearCalibrationCalendarStorage(userId)
    return { repaired: true, error: null }
  }

  const { data: metrics, error: metricsError } = await supabase
    .from('calibration_metrics')
    .select('session_type')
    .eq('user_id', userId)

  if (metricsError) {
    return { repaired: false, error: metricsError.message }
  }

  const recordedTypes = (metrics ?? []).map((row) => row.session_type as CalibrationSessionType)
  const validCount = consecutiveCompletedSessions(recordedTypes)
  const orphans = orphanCalibrationMetrics(recordedTypes)
  const inconsistent = orphans.length > 0 || dbCount !== validCount

  if (!inconsistent) {
    return { repaired: false, error: null }
  }

  if (orphans.length > 0) {
    const { error: deleteError } = await supabase
      .from('calibration_metrics')
      .delete()
      .eq('user_id', userId)
      .in('session_type', orphans)

    if (deleteError) {
      return { repaired: false, error: deleteError.message }
    }
  }

  if (dbCount !== validCount) {
    const { error: updateError } = await supabase
      .from('profiles')
      .update({
        calibration_completed_sessions: validCount,
        calibration_status: validCount === 0 ? 'not_started' : 'in_progress',
      })
      .eq('id', userId)

    if (updateError) {
      return { repaired: false, error: updateError.message }
    }
  }

  clearCalibrationCalendarStorage(userId)
  return { repaired: true, error: null }
}
